import { createContext, useContext, useEffect, useState } from "react";
import { useQuery, useQueryClient } from "react-query";

import { usePageNavigation } from "../components/dataTable/hooks/usePageNavigation";
import { QueryRequest, QueryResult, SortCriterion } from "../types/responses";
import { UserDataContext } from "./userDataContext";

export const DataTableServerSideContext = createContext({});

export const extractDefaultFilters = (
	columnSettings: any,
): Record<string, any> => {
	const defaultFilters: Record<string, any> = {};

	if (columnSettings?.columns) {
		columnSettings.columns.forEach((column: any) => {
			if (column.defaultFilterValue !== undefined) {
				const key = column.id || column.accessor;
				if (key) {
					defaultFilters[key] = column.defaultFilterValue;
				}
			}
		});
	}

	return defaultFilters;
};

export const DataTableServerSideProvider = ({
	children,
	initialIsErrorState = false,
	apiFunction,
	dataTableColumnSettings,
	dataTableEditorSettings,
	triggerRefresh = null,
	isLoading = false,
	queryKey = "queryKey",
	refreshKey = queryKey,
	isEnabled = true,
}: {
	children: any;
	initialIsErrorState?: boolean | null;
	apiFunction: ({
		filterCriteria,
		sortCriteria,
		pageNo,
		pageSize,
	}: QueryRequest) => Promise<QueryResult>;
	dataTableColumnSettings: any;
	dataTableEditorSettings?: any;
	triggerRefresh?: boolean | null;
	isLoading?: boolean | null;
	queryKey?: string | string[];
	refreshKey?: string | string[];
	isEnabled?: boolean;
}) => {
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [modalAction, setOrgModalAction] = useState("");
	const [initialModalData, setInitialModalData] = useState([{}]);
	const [isSelect, setIsSelect] = useState(false);

	const queryKeys = Array.isArray(queryKey) ? queryKey : [queryKey];
	const refreshKeys = Array.isArray(refreshKey) ? refreshKey : [refreshKey];

	const [filterCriteria, setFilterCriteria] = useState<
		Record<string, object>
	>(extractDefaultFilters(dataTableColumnSettings));
	const [sortCriteria, setSortCriteria] = useState([
		{ field: dataTableColumnSettings.columns[0].id, isAscending: true },
	] as SortCriterion[]);

	const pagination = usePageNavigation();
	const { pageNo, pageSize, setFromQueryData } = pagination;

	const { selectedOrgId } = useContext(UserDataContext);
	const {
		isLoading: isTableLoading,
		isFetching,
		error,
		data,
	} = useQuery(
		[
			...queryKeys,
			"selectedOrgId",
			selectedOrgId,
			"filters",
			filterCriteria,
			"pageNo",
			pageNo,
			"pageSize",
			pageSize,
			"sortCriteria",
			sortCriteria,
		],
		() => apiFunction({ filterCriteria, pageNo, pageSize, sortCriteria }),
		{
			enabled: isEnabled,
			keepPreviousData: true,
		},
	);

	const queryData =
		data ||
		({
			tableData: [],
			fetchedCount: 0,
			totalAvailable: 0,
			paginationMetadata: { pageNo: 1, totalPages: 1 },
		} as QueryResult);

	useEffect(() => {
		if (queryData && isFetching === false) {
			setFromQueryData(queryData);
		}
	}, [queryData, setFromQueryData]);

	const tableData = queryData.tableData;

	let dataError = null;
	if (!Array.isArray(tableData)) {
		dataError = "Bad data";
		console.error(dataError);
	}

	const fetched = queryData?.fetchedCount || tableData.length;
	const totalAvailable = queryData?.totalAvailable || tableData.length;

	const queryClient = useQueryClient();

	const refreshTableData = () =>
		queryClient.invalidateQueries({ queryKey: [...refreshKeys] });

	useEffect(() => {
		if (data && !isFetching && !error) {
			const totalPagesCount = queryData.paginationMetadata.totalPages;
			const pagesToPrefetch: number[] = [];

			// Always prefetch first page (if not current)
			if (pageNo !== 1) {
				pagesToPrefetch.push(1);
			}

			// Always prefetch last page (if not current and exists)
			if (pageNo !== totalPagesCount && totalPagesCount > 1) {
				pagesToPrefetch.push(totalPagesCount);
			}

			// Prefetch previous 2 pages
			if (pageNo > 1) {
				pagesToPrefetch.push(pageNo - 1);
			}
			if (pageNo > 2) {
				pagesToPrefetch.push(pageNo - 2);
			}

			// Prefetch next 2 pages
			if (pageNo < totalPagesCount) {
				pagesToPrefetch.push(pageNo + 1);
			}
			if (pageNo < totalPagesCount - 1) {
				pagesToPrefetch.push(pageNo + 2);
			}

			// Remove duplicates and prefetch
			const uniquePages = [...new Set(pagesToPrefetch)];

			uniquePages.forEach((prefetchPageNo) => {
				queryClient.prefetchQuery(
					[
						...queryKeys,
						"selectedOrgId",
						selectedOrgId,
						"filters",
						filterCriteria,
						"pageNo",
						prefetchPageNo,
						"pageSize",
						pageSize,
						"sortCriteria",
						sortCriteria,
					],
					() =>
						apiFunction({
							filterCriteria,
							pageNo: prefetchPageNo,
							pageSize,
							sortCriteria,
						}),
					{
						staleTime: 5 * 60 * 1000, // Cache for 5 minutes
					},
				);
			});
		}
	}, [
		pageNo,
		pageSize,
		filterCriteria,
		selectedOrgId,
		data,
		isFetching,
		queryClient,
		queryKeys,
		error,
		apiFunction,
		queryData.paginationMetadata.totalPages,
	]);

	useEffect(() => {
		if (triggerRefresh !== null) refreshTableData();
	}, [triggerRefresh]);

	// Reset to page 1 when filters change
	// useEffect(() => {
	// 	resetToFirstPage();
	// }, [filterCriteria]);

	return (
		<DataTableServerSideContext.Provider
			value={{
				isModalOpen,
				setIsModalOpen,
				modalAction,
				setOrgModalAction,
				pagination,
				sortCriteria,
				setSortCriteria,
				initialModalData,
				setInitialModalData,
				isLoading: isTableLoading || isLoading,
				isFetching,
				tableData,
				isError: initialIsErrorState || error || dataError,
				dataTableColumnSettings,
				dataTableEditorSettings,
				paginationData: { fetched, totalAvailable },
				refreshTableData,
				filterCriteria,
				setFilterCriteria,
				isSelect,
				setIsSelect,
			}}
		>
			{children}
		</DataTableServerSideContext.Provider>
	);
};
